import { readItems, createItem, aggregate } from "@directus/sdk";
import directus from "@/lib/directus";
import { CONTEST_CONFIG } from "@/config/contestConfig";

export const parrainageService = {
  fetchParrainageList: async (numero_commercial: string) => {
    try {
      const parrainages = await directus.request(
        readItems("parrainage", {
          filter: {
            numero_commercial: {
              _eq: numero_commercial,
            },
          },
          fields: ["client_id", "date_created", "date_updated", "numero_commercial"],
          sort: ["-date_created"],
        })
      );
      return {
        success: true,
        parrainages,
      };
    } catch (error: any) {
      return {
        success: false,
        error: new Error(error.message || "Erreur lors de la récupération des parrainages"),
      };
    }
  },

  addParrainage: async ({ numero_commercial, client_id }: { numero_commercial: string; client_id: string }) => {
    try {
      // filter deposit to check if users it not already an existing client more than once deposit or more than a week
      const deposit = await directus.request(
        readItems("depot", {
          filter: {
            account_id: {
              _eq: client_id,
            },
          },
          fields: ["account_id", "date_created", "date_updated", "numero_commercial"],
          sort: ["-date_created"],
        })
      );
      if (deposit.length > 0) {
        return {
          success: false,
          error: new Error("ce client est deja parrainé"),
        };
      }
      await directus.request(
        createItem("parrainage", {
          numero_commercial,
          client_id,
        })
      );
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: new Error(error.message || "Erreur lors de l'ajout du parrainage"),
      };
    }
  },

  /**
   * Récupère les dépôts effectués par les filleuls d'un parrain.
   * Les filleuls sont identifiés par leur client_id, qui correspond
   * au champ `account_id` de la collection `depot`.
   */
  fetchFilleulsDeposits: async (clientIds: string[]) => {
    try {
      if (!clientIds || clientIds.length === 0) {
        return { success: true, depots: [] as any[] };
      }

      const depots = await directus.request(
        readItems("depot", {
          filter: {
            account_id: {
              _in: clientIds,
            },
          },
          sort: ["-date_created"],
          limit: -1,
        })
      );

      return {
        success: true,
        depots: (depots as any[]) ?? [],
      };
    } catch (error: any) {
      return {
        success: false,
        depots: [] as any[],
        error: new Error(error.message || "Erreur lors de la récupération des dépôts des filleuls"),
      };
    }
  },

  /**
   * Récupère les parrainages avec leur cumul de dépôts et statut de validation
   * Utilisé pour le concours de parrainage - OPTIMISÉ AVEC AGGREGATE
   */
  fetchParrainagesWithDeposits: async (numero_commercial: string, depositThreshold: number = 2500, startDate?: string) => {
    try {
      const contestStartDate = startDate || CONTEST_CONFIG.START_DATE;

      // Récupère les parrainages créés après la date de début du concours
      const parrainages = await directus.request(
        readItems("parrainage", {
          filter: {
            numero_commercial: {
              _eq: numero_commercial,
            },
            date_created: {
              _gte: contestStartDate,
            },
          },
          fields: ["client_id", "date_created", "date_updated", "numero_commercial"],
          sort: ["-date_created"],
        })
      );

      if (parrainages.length === 0) {
        return {
          success: true,
          parrainages: [],
        };
      }

      // Récupère tous les client_ids
      const clientIds = parrainages.map((p: any) => p.client_id);

      // OPTIMISATION: Une seule requête avec aggregate pour tous les dépôts
      const depositsAggregate = await directus.request(
        aggregate("depot", {
          aggregate: {
            sum: ["montant"],
          },
          groupBy: ["account_id"],
          query: {
            filter: {
              account_id: {
                _in: clientIds,
              },
              date_created: {
                _gte: contestStartDate,
              },
            },
          },
        })
      );

      // Créer un map des cumuls de dépôts
      const depositsMap: Record<string, number> = {};
      if (depositsAggregate && Array.isArray(depositsAggregate)) {
        depositsAggregate.forEach((agg: any) => {
          depositsMap[agg.account_id] = agg.sum?.montant || 0;
        });
      }

      // Construire le résultat avec les cumuls
      const parrainagesWithDeposits = parrainages.map((p: any) => {
        const cumulativeDeposits = depositsMap[p.client_id] || 0;
        const isValid = cumulativeDeposits >= depositThreshold;
        const remainingToThreshold = Math.max(0, depositThreshold - cumulativeDeposits);

        return {
          client_id: p.client_id,
          date_created: p.date_created,
          date_updated: p.date_updated,
          numero_commercial: p.numero_commercial,
          cumulative_deposits: cumulativeDeposits,
          is_valid: isValid,
          remaining_to_threshold: remainingToThreshold,
        };
      });

      return {
        success: true,
        parrainages: parrainagesWithDeposits,
      };
    } catch (error: any) {
      console.error("Error in fetchParrainagesWithDeposits:", error);
      return {
        success: false,
        error: new Error(error.message || "Erreur lors de la récupération des parrainages"),
      };
    }
  },
};
